import './styles';
import {Box, Link, Typography} from '@mui/material';
import {Autocomplete, Libraries, LoadScriptNext} from '@react-google-maps/api';
import {useTranslation} from 'react-i18next';

import {AllowedCountryCodesSignIn} from '@/app/auth/constants/AllowedPhoneNumbers';
import {
  AppButton,
  ButtonContainer,
  PhoneInputTextField,
} from '@/app/shared-components';
import InputTextField from '@/app/shared-components/InputTextField/InputTextField';
import PoweredByModak from '@/app/shared-components/PoweredByModak/PoweredByModak';
import {
  allowedAutocompleteTypes,
  defaultAddressCountry,
} from '@/app/utils/AddressUtils/AddressUtils';
import {ENV} from '@/constants/EnvConstants';
import {LINKS} from '@/constants/LinksConstants';
import {IFee} from '@/dataSource/src/api/payment-link/entities';
import useWindowSize from '@/hooks/WindowSize/WindowSize';
import {validateNameInput} from '@/utils/validateNames';

import PaymentLinkFooter, {
  FOOTER_DATE,
} from '../PaymentLinkFooter/PaymentLinkFooter';

import {useActions} from './useActions';

const libraries: Libraries = ['places'];

interface PaymentLinkFormProps {
  name: string;
  selectedFee: IFee;
  amount: number;
  isSelectTabAmount: boolean;
}

export const PaymentLinkForm: React.FC<PaymentLinkFormProps> = ({
  name,
  selectedFee,
  amount,
  isSelectTabAmount,
}) => {
  const {t} = useTranslation();
  const calculatedFee =
    amount * (selectedFee.percentage_fee / 100) + selectedFee.flat_fee;
  const {
    autoCompletePaymentRef,
    handlePlaceChanged,
    handleSetValue,
    formAddress,
    setFormAddress,
    disableButton,
    onButtonPress,
    addressFormErrors,
    handleTrackTermsClick,
    setEmailError,
  } = useActions({amount, selectedFee, isSelectTabAmount});
  const {isMobile} = useWindowSize();
  const loadingGooglePlacesElement = (
    <div>{t('payment_link.form.loading_google_places')}</div>
  );

  return (
    <>
      <Box className="payment-link-form-container">
        <Box className="payment-link-form-sides-container">
          <Box className="payment-link-form-right-content">
            <Box>
              <Typography className="payment-link-form-title">
                {t('payment_link.form.title')}
              </Typography>
            </Box>
            <Box className="payment-link-form-names-container">
              <InputTextField
                label={t('payment_link.form.first_name')}
                placeholder={t('payment_link.form.first_name')}
                maxLength={50}
                value={formAddress.firstName}
                setValue={(value: string) =>
                  setFormAddress({firstName: validateNameInput(value)})
                }
                onBlur={() =>
                  setFormAddress({firstName: formAddress.firstName.trim()})
                }
              />
              <InputTextField
                label={t('payment_link.form.last_name')}
                placeholder={t('payment_link.form.last_name')}
                maxLength={50}
                value={formAddress.lastName}
                setValue={(value: string) =>
                  setFormAddress({lastName: validateNameInput(value)})
                }
                onBlur={() =>
                  setFormAddress({lastName: formAddress.lastName.trim()})
                }
              />
            </Box>
            <Box className="payment-link-form-field-container">
              <InputTextField
                label={t('payment_link.form.email')}
                placeholder={t('payment_link.form.email')}
                value={formAddress.email}
                type={'email'}
                setValue={(value: string) => setFormAddress({email: value})}
                onErrorChange={setEmailError}
              />
            </Box>
            <PhoneInputTextField
              value={formAddress.phone_number}
              onChange={(value: string) =>
                setFormAddress({phone_number: value})
              }
              countries={AllowedCountryCodesSignIn}
              showCountryCode={true}
            />
            <Box>
              <LoadScriptNext
                loadingElement={loadingGooglePlacesElement}
                googleMapsApiKey={ENV.VITE_GOOGLE_PLACES_KEY}
                libraries={libraries}>
                <Autocomplete
                  onLoad={autocomplete =>
                    (autoCompletePaymentRef.current = autocomplete)
                  }
                  options={{
                    types: allowedAutocompleteTypes,
                    componentRestrictions: {country: defaultAddressCountry},
                  }}
                  onPlaceChanged={handlePlaceChanged}
                  className="payment-link-form-autocomplete">
                  <InputTextField
                    label={t('payment_link.form.address_line_1_label')}
                    placeholder={t('payment_link.form.address_line_1_label')}
                    helperError={t('payment_link.form.address_line_1_error')}
                    error={!!addressFormErrors.addressLine1}
                    value={formAddress.addressLine1}
                    setValue={handleSetValue}
                    style={{
                      width: '100%',
                      fontSize: '16px',
                    }}
                  />
                </Autocomplete>
              </LoadScriptNext>
            </Box>
            <>
              <InputTextField
                label={t('payment_link.form.address_line_2_label')}
                placeholder={t('payment_link.form.address_line_2_label')}
                maxLength={50}
                value={formAddress.addressLine2}
                setValue={(value: string) =>
                  setFormAddress({addressLine2: value})
                }
                style={{
                  width: '100%',
                  fontSize: '16px',
                }}
              />

              <InputTextField
                label={t('payment_link.form.city')}
                placeholder={t('payment_link.form.city')}
                value={formAddress.city}
                setValue={(value: string) => setFormAddress({city: value})}
                style={{
                  width: '100%',
                  fontSize: '16px',
                }}
                error={!!addressFormErrors.city}
              />
              <Box className="payment-link-form-address-state-zip-container">
                <InputTextField
                  label={t('payment_link.form.state')}
                  placeholder={t('payment_link.form.state')}
                  value={formAddress.state}
                  setValue={(value: string) => setFormAddress({state: value})}
                  style={{
                    width: '100%',
                    fontSize: '16px',
                  }}
                  disabled
                  error={!!addressFormErrors.state}
                />
                <InputTextField
                  label={t('payment_link.form.zip_code')}
                  placeholder={t('payment_link.form.zip_code')}
                  value={formAddress.zipCode}
                  setValue={(value: string) => setFormAddress({zipCode: value})}
                  style={{
                    width: '100%',
                    fontSize: '16px',
                  }}
                  disabled
                  error={!!addressFormErrors.zipCode}
                />
              </Box>
            </>
            <Box>
              <Typography className="payment-link-form-disclaimer">
                {t('payment_link.form.continue_terms')}{' '}
                <Link
                  onClick={handleTrackTermsClick}
                  href={LINKS.REQUEST_MONEY_TERMS}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="payment-link-form-disclaimer-terms">
                  {t('payment_link.form.terms_and_conditions')}
                </Link>
              </Typography>
            </Box>
            {!isMobile && (
              <Box className="payment-link-form-button-container">
                <AppButton
                  label={t('payment_link.form.button')}
                  disabled={disableButton}
                  onClick={onButtonPress}
                />
              </Box>
            )}
          </Box>
          <Box className="payment-link-form-left-content">
            <Box className="payment-link-form-left-content-container">
              <Box className="payment-link-form-send-container">
                <Typography className="payment-link-form-send">
                  {t('payment_link.form.sending_money', {name})}
                </Typography>
              </Box>
              <Box className="payment-link-form-amount-container">
                <Typography className="payment-link-form-amount-left">
                  {t('payment_link.form.receives', {name})}
                </Typography>
                <Typography className="payment-link-form-amount-right">
                  ${amount.toFixed(2)}
                </Typography>
              </Box>
              <Box className="payment-link-form-amount-container">
                <Typography className="payment-link-form-amount-left">
                  {t('payment_link.form.transfer_fee')}
                </Typography>
                <Typography className="payment-link-form-amount-right">
                  ${calculatedFee.toFixed(2)}
                </Typography>
              </Box>
              <Box className="payment-link-form-divider" />
              <Box className="payment-link-form-amount-container">
                <Typography className="payment-link-form-amount-left">
                  {t('payment_link.form.you_pay')}
                </Typography>
                <Typography className="payment-link-form-amount-right-total">
                  ${(calculatedFee + amount).toFixed(2)}
                </Typography>
              </Box>
            </Box>
            <Box className="payment-link-form-security-container">
              <Box className="payment-link-form-security-image-container">
                <Box
                  component="img"
                  src="/assets/icons/secure-encrypt.svg"
                  sx={{
                    width: 29,
                    height: 29,
                  }}
                />
                <Box className="payment-link-form-security-labels-container">
                  <Typography className="payment-link-form-security-title">
                    {t('payment_link.secure')}
                  </Typography>
                  <Typography className="payment-link-form-security-subtitle">
                    {t('payment_link.tls_encryption')}
                  </Typography>
                </Box>
              </Box>
              <Typography className="payment-link-form-security-text">
                {t('payment_link.security_text')}
              </Typography>
            </Box>
          </Box>
        </Box>
        {isMobile && (
          <Box className="payment-link-form-button-container">
            <ButtonContainer sticky>
              <AppButton
                label={t('payment_link.form.button')}
                disabled={disableButton}
                onClick={onButtonPress}
              />
            </ButtonContainer>
          </Box>
        )}
      </Box>
      <PaymentLinkFooter
        footerTextComponent={
          <Typography className="payment-link-footer-text payment-link-footer-text-first">
            {t('payment_link.first_label_footer', {date: FOOTER_DATE})}
          </Typography>
        }
        footerRightComponent={<PoweredByModak />}
      />
    </>
  );
};
